import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import { Provider } from './context';
import { size } from '@/helpers/lodash';
import { getLocalStorageItem, setLocalStorageItem } from '@/helpers/localStorage';
import useInterval from '@/hooks/utility/useInterval';
import useABFeatureFromLocalStorage from '@/hooks/user/useABFeatureFromLocalStorage';
import { FEATURE_KEY_UNLIMITED_SKIPS } from '@/config/feature-toggles/config';

// Use an obscure name so that it's not obvious that this controls audio skip limits
const LOCAL_STORAGE_SKIP_KEY = 'kasl';
const MAX_SKIPS_ALLOWED = 6;
const ADMIN_MAX_SKIPS_ALLOWED = 999;
// How long before a skip is restored to the user
const MAX_SKIP_TIME_LIMIT_MS = 60 * 60 * 1000; // 60 minutes
// How often to check in local storage if a skip has been restored to the user
const CHECK_FOR_SKIPS_AVAILABLE_INTERVAL_MS = 1 * 60 * 1000; // 1 minute

const calculateMinutesUntilNextSkipAvailable = ({ skips = [] } = {}) => {
  const oldestSkip = skips[skips.length - 1];
  const msSinceOldestSkip = Date.now() - skips[skips.length - 1];
  const minutesSinceOldestSkip = Math.floor(msSinceOldestSkip / (1000 * 60));
  const skipRenewalTimeFrameMinutes = (MAX_SKIP_TIME_LIMIT_MS / (1000 * 60));
  const timeUntilNextCheckMinutes = CHECK_FOR_SKIPS_AVAILABLE_INTERVAL_MS / (1000 * 60);

  return oldestSkip
    ? (skipRenewalTimeFrameMinutes - minutesSinceOldestSkip)
    : timeUntilNextCheckMinutes;
};

const getCurrentSkipsExcludingStale = () => {
  const currentSkips = getLocalStorageItem(LOCAL_STORAGE_SKIP_KEY) || [];

  // Exclude any skips that were more than an hour ago
  return currentSkips.filter((timestamp) => (Date.now() - timestamp) < MAX_SKIP_TIME_LIMIT_MS);
};

const AudioTrackSkipsProvider = ({ children }) => {
  const [skips, setSkips] = useState([]);
  const {
    isEnabled: hasAdminStatus,
    isLoading: adminStatusLoading
  } = useABFeatureFromLocalStorage({ featureKey: FEATURE_KEY_UNLIMITED_SKIPS });

  const setCurrentSkips = useCallback((newSkips = []) => {
    setSkips(newSkips);
    setLocalStorageItem({
      key: LOCAL_STORAGE_SKIP_KEY,
      value: newSkips
    });
  }, []);

  // Initially populate skips from local storage
  useEffect(() => {
    setCurrentSkips(getCurrentSkipsExcludingStale());
  }, [setCurrentSkips]);

  const handleCheckForSkipsAvailable = useCallback(() => {
    setCurrentSkips(getCurrentSkipsExcludingStale());
  }, [setCurrentSkips]);

  const saveSkipInLocalStorage = useCallback(() => {
    const currentSkips = getCurrentSkipsExcludingStale();
    const newSkips = [
      Date.now(),
      ...currentSkips
    ];

    setCurrentSkips(newSkips);
  }, [setCurrentSkips]);

  useInterval({
    callback: handleCheckForSkipsAvailable,
    delay: CHECK_FOR_SKIPS_AVAILABLE_INTERVAL_MS
  });

  const maxSkipsAllowed = (!adminStatusLoading && hasAdminStatus) ? ADMIN_MAX_SKIPS_ALLOWED : MAX_SKIPS_ALLOWED;
  const skipsRemaining = useMemo(() => Math.max(maxSkipsAllowed - size(skips), 0), [skips, maxSkipsAllowed]);
  const minutesUntilNextSkipAvailable = useMemo(() => calculateMinutesUntilNextSkipAvailable({ skips }), [skips]);

  const value = useMemo(() => ({
    saveSkipInLocalStorage,
    maxSkipsAllowed,
    skipsRemaining,
    skips,
    minutesUntilNextSkipAvailable
  }), [saveSkipInLocalStorage, skipsRemaining, skips, minutesUntilNextSkipAvailable, maxSkipsAllowed]);

  return (
    <Provider value={ value }>
      { children }
    </Provider>
  );
};

AudioTrackSkipsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export default AudioTrackSkipsProvider;